import type { ReactNode } from 'react';
import * as R from 'remeda';
import type { AuditLogEntry } from '@rossum/api-client/auditLogs';
import { MAX_GROUP_GAP_MS } from '../constants';
import type {
  ActivityAdapter,
  ActivityBlock,
  ActivityRow,
  ActivitySource,
  ActorRef,
} from '../model';
import {
  toActivitySource,
  toActorRefs,
  toActorResolver,
} from './sourceAttribution';

// `content.requestId` cannot replace this: on develop it is ~1:1 with entries (51 distinct
// ids across 52 entries, one null), because each field save is its own request.
const sameSource = (a: ActivitySource, b: ActivitySource) =>
  a.actor === b.actor && a.sourceType === b.sourceType && a.hookId === b.hookId;

// NaN comparisons are false, so an unparseable timestamp merges rather than splits.
const exceedsGap = (newer: string, older: string) =>
  Date.parse(newer) - Date.parse(older) > MAX_GROUP_GAP_MS;

type EntryGroup = {
  source: ActivitySource;
  newestTimestamp: string;
  entries: AuditLogEntry[];
};

const groupEntries = (entries: AuditLogEntry[]): EntryGroup[] =>
  entries.reduce<EntryGroup[]>((groups, entry) => {
    const source = toActivitySource(entry);
    const last = groups[groups.length - 1];
    const previous = last?.entries[last.entries.length - 1];
    if (
      last &&
      previous &&
      sameSource(last.source, source) &&
      // Entries are newest-first, so `previous` is the newer neighbour.
      !exceedsGap(previous.timestamp, entry.timestamp)
    ) {
      last.entries.push(entry);
      return groups;
    }
    return [
      ...groups,
      { source, newestTimestamp: entry.timestamp, entries: [entry] },
    ];
  }, []);

// Entries group by display name, so two hooks sharing a name but not an id both end up here.
const toActors = ({
  entries,
  resolve,
}: {
  entries: AuditLogEntry[];
  resolve: (ref: ActorRef) => ActorRef;
}): ActorRef[] =>
  R.pipe(
    entries,
    R.flatMap(toActorRefs),
    R.map(resolve),
    R.uniqueBy(actor => actor.id)
  );

const toRequestIds = (entries: AuditLogEntry[]): string[] =>
  R.pipe(
    entries,
    R.map(entry => entry.content.requestId),
    R.filter(R.isTruthy),
    R.unique()
  );

type AdapterOutput = { rows: ActivityRow[]; entries: AuditLogEntry[] };

const runAdapter = ({
  adapter,
  group,
  allEntries,
}: {
  adapter: ActivityAdapter;
  group: EntryGroup;
  allEntries: AuditLogEntry[];
}): AdapterOutput => {
  const perEntry = group.entries.flatMap((entry, entryIndex) => {
    const entryRows = adapter.toRows(entry, {
      entries: allEntries,
      index: allEntries.indexOf(entry),
      entryIndex,
    });
    return entryRows.length > 0 ? [{ entry, rows: entryRows }] : [];
  });
  const groupRows = perEntry.flatMap(({ rows }) => rows);
  return {
    rows: adapter.combineRows ? adapter.combineRows(groupRows) : groupRows,
    entries: perEntry.map(({ entry }) => entry),
  };
};

const renderWith =
  (adapters: ActivityAdapter[]) =>
  (row: ActivityRow): ReactNode =>
    adapters.reduce<ReactNode>(
      (rendered, adapter) => rendered ?? adapter.renderRow(row),
      null
    );

// Orchestrator: one block per group, its rows in adapter order. Adapters are
// injected (registry composed at the edge) so core never imports from features/.
export const toActivityBlocks = (
  entries: AuditLogEntry[],
  adapters: ActivityAdapter[]
): ActivityBlock[] => {
  const resolveActor = toActorResolver(entries);
  const renderRow = renderWith(adapters);
  return groupEntries(entries).flatMap((group, index) => {
    const outputs = adapters.map(adapter =>
      runAdapter({ adapter, group, allEntries: entries })
    );
    const rows = outputs.flatMap(output => output.rows);
    if (rows.length === 0) return [];
    return [
      {
        // Groups carry no stable id on the wire — newest timestamp + position is
        // unique within one fetched result set.
        id: `${group.newestTimestamp}:${index}`,
        timestamp: group.newestTimestamp, // newest entry in the group
        source: group.source,
        actors: toActors({ entries: group.entries, resolve: resolveActor }),
        rows,
        renderRow,
        requestIds: toRequestIds(
          R.unique(outputs.flatMap(output => output.entries))
        ),
      },
    ];
  });
};
